var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { create, get } from '@github/webauthn-json';
import { fidoAuthenticatorToMfaMethod, mfaMethodToFidoAuthenticator } from '../transforms';
let unboundAuthenticator;
/**
 * Returns the first usable authenticator (bounded or unbounded, in that priority).
 */
export function getAuthenticator(method) {
    var _a, _b, _c;
    const mfaMethod = ((_a = method === null || method === void 0 ? void 0 : method.methodAttributes) === null || _a === void 0 ? void 0 : _a.boundMfaMethod) || ((_c = (_b = method === null || method === void 0 ? void 0 : method.methodAttributes) === null || _b === void 0 ? void 0 : _b.unboundMfaMethods) === null || _c === void 0 ? void 0 : _c[0]);
    if (mfaMethod) {
        return mfaMethodToFidoAuthenticator(mfaMethod);
    }
}
/**
 * Returns true if the device is recognized for FIDO authentication.
 *
 * @public
 */
export function isFidoDeviceRecognized(authenticationMethod) {
    return !!getAuthenticator(authenticationMethod);
}
/**
 * Returns true if the given FIDO method has a device-bounded authenticator, REGARDLESS if it is
 * account-bound or not.
 *
 * @public
 */
export function isFidoDeviceBounded(method) {
    var _a;
    return !!((_a = method === null || method === void 0 ? void 0 : method.methodAttributes) === null || _a === void 0 ? void 0 : _a.boundMfaMethod);
}
/**
 * Returns true if the given FIDO method has a device-bounded authenticator, but does NOT have it
 * account-bound.
 *
 * @public
 */
export function hasUnboundedFidoCredentials(method) {
    var _a, _b;
    return !!((_b = (_a = method === null || method === void 0 ? void 0 : method.methodAttributes) === null || _a === void 0 ? void 0 : _a.unboundMfaMethods) === null || _b === void 0 ? void 0 : _b.length);
}
/**
 * Returns true if FIDO (i.e., webAuthN) is supported on this device.
 *
 * @public
 */
export function isFidoSupported() {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            if (typeof window.PublicKeyCredential !== 'undefined') {
                return window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
            }
            return false;
        }
        catch (_) {
            return false;
        }
    });
}
/**
 * Returns true if Secure Payment Confirmation (SPC) is supported on this device.
 */
export function isSecurePaymentConfirmationSupported() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!('PaymentRequest' in window)) {
            return false;
        }
        try {
            return new PaymentRequest([
                {
                    supportedMethods: 'secure-payment-confirmation',
                    data: {}
                }
            ], {
                total: {
                    label: 'Total',
                    amount: {
                        currency: 'USD',
                        value: '0'
                    }
                }
            }).canMakePayment();
        }
        catch (_) {
            return false;
        }
    });
}
/**
 * Requests WebAuthn to create a new public key credential.
 */
export function createPublicKey(options) {
    var _a;
    return create({
        publicKey: Object.assign(Object.assign({}, options), { authenticatorSelection: Object.assign(Object.assign({}, options.authenticatorSelection), { residentKey: ((_a = options.authenticatorSelection) === null || _a === void 0 ? void 0 : _a.requireResidentKey) ? 'required' : undefined }), extensions: sanitizeExtensions(options.extensions) })
    });
}
/**
 * Requests the existing public key credential from WebAuthn.
 */
export function getPublicKey(options) {
    var _a;
    return get({
        publicKey: Object.assign(Object.assign({}, options), { 
            // TODO: follow up with DAS team to clean this up
            allowCredentials: (_a = options === null || options === void 0 ? void 0 : options.allowCredentials) === null || _a === void 0 ? void 0 : _a.map((allowedCredential) => (Object.assign(Object.assign({}, allowedCredential), { transports: allowedCredential.transports || [] }))), extensions: sanitizeExtensions(options === null || options === void 0 ? void 0 : options.extensions) })
    });
}
/**
 * Returns the KPI
 */
export function getMfaMethodKpiMeta(authenticationMethod, deviceData, authenticator) {
    var _a, _b, _c, _d, _e, _f, _g;
    if (!authenticationMethod)
        return {};
    unboundAuthenticator = unboundAuthenticator || authenticator;
    const unBoundMfaMethod = unboundAuthenticator && fidoAuthenticatorToMfaMethod(unboundAuthenticator).mfaMethod;
    const boundMfaMethod = (_b = (_a = authenticationMethod === null || authenticationMethod === void 0 ? void 0 : authenticationMethod.methodAttributes) === null || _a === void 0 ? void 0 : _a.boundMfaMethod) === null || _b === void 0 ? void 0 : _b.mfaMethod;
    const mfaMethod = boundMfaMethod || unBoundMfaMethod;
    return {
        type: authenticationMethod === null || authenticationMethod === void 0 ? void 0 : authenticationMethod.authenticationMethodType,
        certifiedMfaMethodId: mfaMethod === null || mfaMethod === void 0 ? void 0 : mfaMethod.certifiedMfaMethodId,
        externalMfaMethodInstanceId: mfaMethod === null || mfaMethod === void 0 ? void 0 : mfaMethod.externalMfaMethodInstanceId,
        mfaMethodInstanceId: mfaMethod === null || mfaMethod === void 0 ? void 0 : mfaMethod.mfaMethodInstanceId,
        fidoMfaMethodData: {
            relyingPartyInfoId: (_d = (_c = mfaMethod === null || mfaMethod === void 0 ? void 0 : mfaMethod.fidoMfaMethodData) === null || _c === void 0 ? void 0 : _c.relyingPartyInfo) === null || _d === void 0 ? void 0 : _d.id,
            fidoUserName: (_e = mfaMethod === null || mfaMethod === void 0 ? void 0 : mfaMethod.fidoMfaMethodData) === null || _e === void 0 ? void 0 : _e.fidoUserName,
            fidoUserId: (_f = mfaMethod === null || mfaMethod === void 0 ? void 0 : mfaMethod.fidoMfaMethodData) === null || _f === void 0 ? void 0 : _f.fidoUserId,
            isSpcEnabled: !!((_g = mfaMethod === null || mfaMethod === void 0 ? void 0 : mfaMethod.fidoMfaMethodData) === null || _g === void 0 ? void 0 : _g.isSpcEnabled),
            isSpcSupported: deviceData === null || deviceData === void 0 ? void 0 : deviceData.spcSupported,
            isDeviceRecognizedForFido: !!(deviceData === null || deviceData === void 0 ? void 0 : deviceData.isDeviceRecognizedForFido)
        }
    };
}
/**
 * Removes all `null` or `undefined` PublicKeyCredential extension options.
 *
 * TODO: follow up with DAS team to see if we can clean this up.
 */
function sanitizeExtensions(extensions) {
    if (!extensions)
        return;
    return Object.keys(extensions)
        .filter((key) => extensions[key] != null)
        .reduce((sanitized, key) => (Object.assign(Object.assign({}, sanitized), { [key]: extensions[key] })), {
        // allow rp credentials to work on localhost
        enforceCredentialProtectionPolicy: false
    });
}
